import clsx from 'clsx';
import * as React from 'react';

import { resolveLocationDescriptorToURL } from '@gitbook/app-navigation';
import type { BlockHeadingData } from '@gitbook/doc-core';
import {
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    type BLOCK_HEADING_TYPES,
    BLOCK_HINT,
    BLOCK_QUOTE,
    BLOCK_STEPPER_STEP,
    BLOCK_UPDATE,
} from '@gitbook/doc-core';
import { createPalette } from '@gitbook/react-uikit';
import type { AncestorNode, Block } from '@gitbook/slate';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { clipboard, DialogRoot, useDialogTriggerState } from '@gitbook/spine-react';

import { BlockWrapper, useBlockAnchor } from '../common';
import { useTextBlockPaletteFactory } from '../paragraph';
import {
    TextAlignment,
    TextAlignmentContext,
    textAlignmentClassName,
    textAlignmentFlexClassName,
} from '../text-alignment';
import { EditHeadingDialog } from './EditHeadingDialog';

/**
 * Renderer for an heading block.
 */
export function BlockHeading(props: BlockProps): React.ReactElement {
    const { editor, node, parent, children } = props;

    const Tag = headingTags[node.type];

    const editDialogTrigger = useDialogTriggerState();

    const parentTextAlignment = React.useContext(TextAlignmentContext);
    const data = node.data.toFrozenObject() as BlockHeadingData;
    const textAlignment = data?.align ? data.align : parentTextAlignment;
    const textClassName = clsx(textAlignmentClassName[textAlignment], headingClassName[node.type]);

    // Anchor link
    // If a heading is not in a block that supports indexing, we don't want that heading to
    // display an anchor since it won't have one in the outline and shouldn't allow any linking.
    const hasAnchor = parent.type !== BLOCK_QUOTE && parent.type !== BLOCK_HINT;
    const rawBlockAnchor = useBlockAnchor(node, {
        label: 'Direct link to heading',
        iconSizeClassName: 'size-4',
    });
    const blockAnchor = hasAnchor ? rawBlockAnchor : null;
    const anchorPaddingTop = {
        'heading-1': 'pt-1 md:pt-1.5',
        'heading-2': 'pt-0.75 md:pt-1',
        'heading-3': 'pt-0.75',
    }[node.type];

    // Palette
    const extraGroups = React.useMemo(
        () => [
            {
                key: 'heading-link',
                commands: [
                    {
                        key: 'link',
                        title: 'Link',
                        icon: Icon.Link,
                        descendant: () => {
                            return createPalette({
                                position: {
                                    preferredPlacement: 'right',
                                },
                                groups: [
                                    {
                                        key: 'link',
                                        commands: [
                                            {
                                                key: 'edit-anchor',
                                                title: 'Edit anchor',
                                                icon: Icon.Edit,
                                                trigger: editDialogTrigger.open,
                                            },
                                            {
                                                key: 'copy-link',
                                                title: 'Copy link',
                                                icon: Icon.Copy,
                                                trigger: () => {
                                                    const headingURL =
                                                        resolveLocationDescriptorToURL(
                                                            `${window.location.protocol}//${window.location.host}`,
                                                            {
                                                                pathname: window.location.pathname,
                                                                hash: blockAnchor?.blockProps.id,
                                                            }
                                                        );

                                                    clipboard.writeText(headingURL, {
                                                        notify: {
                                                            title: 'Link to heading copied to clipboard',
                                                        },
                                                    });
                                                },
                                            },
                                        ],
                                    },
                                ],
                            });
                        },
                    },
                ],
            },
        ],
        [editDialogTrigger.open, blockAnchor?.blockProps.id]
    );
    const palette = useTextBlockPaletteFactory(node.key, { extraGroups });

    return (
        <>
            <BlockWrapper {...props} palette={palette}>
                <Tag
                    id={blockAnchor?.blockProps.id}
                    className={clsx(
                        'heading relative flex',
                        textAlignmentFlexClassName[textAlignment],
                        blockAnchor?.blockProps.className
                    )}
                >
                    <span
                        id={blockAnchor ? `text-${blockAnchor.blockProps.id}` : undefined}
                        className={clsx('relative min-w-px select-text', textClassName)}
                    >
                        {editor.readOnly && !node.isEmpty && blockAnchor ? (
                            <span
                                className={clsx(
                                    '-left-6 absolute top-0 flex shrink-0',
                                    anchorPaddingTop
                                )}
                            >
                                {blockAnchor.anchorElement}
                            </span>
                        ) : null}

                        {children}
                    </span>

                    {node.isEmpty && textAlignment === TextAlignment.Start ? (
                        <span
                            contentEditable={false}
                            className={clsx(
                                'pointer-events-none absolute select-none text-light',
                                textClassName
                            )}
                        >
                            {getPlaceholder(parent, node)}
                        </span>
                    ) : null}
                </Tag>
            </BlockWrapper>

            <DialogRoot {...editDialogTrigger.rootProps}>
                <EditHeadingDialog node={node} editor={editor} />
            </DialogRoot>
        </>
    );
}

/**
 * Get the placeholder for the heading.
 */
function getPlaceholder(parent: AncestorNode, block: Block): string {
    if (parent.type === BLOCK_STEPPER_STEP && parent.getChildIndex(block.key) === 0) {
        return 'Step title';
    }

    if (parent.type === BLOCK_UPDATE) {
        return 'Product update';
    }

    return headingPlaceholder[block.type];
}

/**
 * Use h2 for BLOCK_HEADING_1 because we use h1 for the title of the document.
 * An html document should only have one h1 tag.
 */
const headingTags: Record<BLOCK_HEADING_TYPES, string> = {
    [BLOCK_HEADING_1]: 'h2',
    [BLOCK_HEADING_2]: 'h3',
    [BLOCK_HEADING_3]: 'h4',
};

const headingPlaceholder: Record<BLOCK_HEADING_TYPES, string> = {
    [BLOCK_HEADING_1]: 'Heading 1',
    [BLOCK_HEADING_2]: 'Heading 2',
    [BLOCK_HEADING_3]: 'Heading 3',
};

const headingClassName: Record<BLOCK_HEADING_TYPES, string> = {
    [BLOCK_HEADING_1]: 'text-content-heading-medium md:text-content-heading-large',
    [BLOCK_HEADING_2]: 'text-content-heading-small md:text-content-heading-medium',
    [BLOCK_HEADING_3]: 'text-content-paragraph md:text-content-heading-small',
};
